import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  apiGetBand, apiGetTours, apiGetBandFiles, apiPutBandPromoContent, apiPutBandPromoSource,
  apiGetTourPromoContent, apiPutTourPromoContent,
  type PromoContentDTO, type BandFileDTO,
} from '../api';
import PromoContentEditor from './PromoContentEditor';
import { PromoContentPage } from '../pages/PromoKitSharePage';

const emptyContent = (): PromoContentDTO => ({ quotes: [], sections: [] });

/** The PUBLIC payload shape PromoContentPage renders (a slice of PromoKitPublicDTO). */
interface PreviewData {
  token: string;
  band: { id: string; name: string; slug: string; image_url: string };
  files: { id: number; filename: string; url: string; size: number; mime: string; uploaded_at: string }[];
}

/**
 * "Promo kit page" authoring block of Band Settings (option C, 2026-10-19):
 *  • a "Content source" select — Band default or one of the band's tours (that
 *    writes `bands.promo_tour_id`; the public page then shows THAT tour's
 *    content, falling back to the band blob when the tour has none);
 *  • the editor of the BAND DEFAULT blob (intro / bio / quotes / contact /
 *    named download sections over the band's promo_kit files);
 *  • a Preview button opening the real public page in a new tab (the token is
 *    idempotent — no new capability is minted).
 * OPTION B (2026-10-19): SPLIT VIEW — the editor sits on the left, the REAL
 * public render (`PromoContentPage`, the exact component of
 * PromoKitSharePage) on the right, re-rendered as the editor is typed
 * (debounced ~300 ms). This is the editor's internal preview, NOT the public
 * page: it needs no token unlock, and its downloads keep pointing at the real
 * GUARDED route (`/api/share/promo/:token/file/:id` — same bytes, same gate).
 * Below ~1100 px the split becomes Éditeur / Aperçu tabs; the preview pane is
 * switchable desktop / mobile (390 px).
 * All writes are editor+; the server re-validates the whole blob band-scoped.
 */
export default function PromoContentSettings({ bandId, token, canWrite }: { bandId: string; token: string | null; canWrite: boolean }) {
  const { t } = useTranslation();
  const [content, setContent] = useState<PromoContentDTO>(emptyContent);
  const [loaded, setLoaded] = useState(false);
  const [tours, setTours] = useState<{ id: number; name: string }[]>([]);
  const [files, setFiles] = useState<BandFileDTO[]>([]);
  const [bandName, setBandName] = useState('');
  const [bandImage, setBandImage] = useState('');
  const [saving, setSaving] = useState(false);
  const [savingSource, setSavingSource] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Live preview (option B): debounced blob + pane mode.
  const [previewContent, setPreviewContent] = useState<PromoContentDTO>(emptyContent);
  const [narrow, setNarrow] = useState(() => (typeof window !== 'undefined' ? window.innerWidth < 1100 : false));
  const [previewTab, setPreviewTab] = useState<'editor' | 'preview'>('editor');
  const [previewWidth, setPreviewWidth] = useState<'desktop' | 'mobile'>('desktop');

  // SOURCE SWITCH BINDING (Sandro v2 addendum, 2026-10-19): the editor edits
  // ONE source at a time — 'band' or a tour id. Switching the select LOADS
  // that source's blob into the fields (asking first when there are unsaved
  // edits), Save writes to the RIGHT endpoint (band blob vs tour blob), and a
  // badge always names the source being edited. Each source gets its own
  // editor instance (React key) so the form state can never leak across
  // sources. `savedSnapshot` is the serialized last-saved/loaded blob of the
  // CURRENT source — the dirty test for the switch confirmation.
  const [target, setTarget] = useState<'band' | number>('band');
  const [bandContent, setBandContent] = useState<PromoContentDTO>(emptyContent);
  const savedSnapshot = useRef<string>('');
  const snapshotOf = (c: PromoContentDTO | null) => JSON.stringify(c || emptyContent());
  const dirty = snapshotOf(content) !== savedSnapshot.current;
  const applyContent = (c: PromoContentDTO | null) => {
    const next = c || emptyContent();
    setContent(next);
    setPreviewContent(next);
    savedSnapshot.current = snapshotOf(next);
  };

  useEffect(() => {
    let cancelled = false;
    setLoaded(false);
    apiGetBand(bandId)
      .then((b) => {
        if (cancelled) return;
        let parsed: PromoContentDTO | null = null;
        try { parsed = b.promo_content ? JSON.parse(b.promo_content) : null; } catch { parsed = null; }
        setBandContent(parsed || emptyContent());
        applyContent(parsed);
        setTarget('band'); // the initial editor edits the BAND default blob
        setBandName(String(b.name || ''));
        setBandImage(String(b.image_url || ''));
        setLoaded(true);
      })
      .catch(() => { if (!cancelled) setLoaded(true); });
    apiGetTours()
      .then((rows) => { if (!cancelled) setTours(rows.filter((r) => String(r.band_id ?? '') === bandId).map((r) => ({ id: Number(r.id), name: r.name }))); })
      .catch(() => { /* the select just stays empty */ });
    apiGetBandFiles(bandId, null, 'promo_kit')
      .then((rows) => { if (!cancelled) setFiles(rows || []); })
      .catch(() => { /* sections simply show no picker */ });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bandId]);

  // Debounced mirror of the blob for the preview (~300 ms per spec).
  useEffect(() => {
    const id = window.setTimeout(() => setPreviewContent(content), 300);
    return () => window.clearTimeout(id);
  }, [content]);

  // Split view ⇄ tabs: below ~1100 px there is no room for two panes.
  useEffect(() => {
    if (typeof window.matchMedia !== 'function') { setNarrow(window.innerWidth < 1100); return; }
    const mq = window.matchMedia('(max-width: 1099px)');
    const on = () => setNarrow(mq.matches);
    on();
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, []);

  // Save writes to the RIGHT target: the band default blob or the selected
  // tour's blob (Sandro v2 addendum — the fields must reflect the source that
  // Save will hit).
  const save = async () => {
    if (!canWrite || saving) return;
    setSaving(true); setMsg(null); setError(null);
    try {
      if (target === 'band') {
        const r = await apiPutBandPromoContent(bandId, content);
        const saved = r.content || emptyContent();
        setBandContent(saved);
        applyContent(saved);
      } else {
        const r = await apiPutTourPromoContent(target, content);
        applyContent(r.content || emptyContent());
      }
      setMsg(t('promoContent.saved'));
    } catch (e) {
      setError(e instanceof Error ? e.message : t('promoContent.saveFailed'));
    } finally { setSaving(false); }
  };

  const clearContent = async () => {
    if (!canWrite || saving) return;
    setSaving(true); setMsg(null); setError(null);
    try {
      if (target === 'band') {
        await apiPutBandPromoContent(bandId, null);
        setBandContent(emptyContent());
      } else {
        await apiPutTourPromoContent(target, null);
      }
      applyContent(emptyContent());
      setMsg(t('promoContent.cleared'));
    } catch (e) {
      setError(e instanceof Error ? e.message : t('promoContent.saveFailed'));
    } finally { setSaving(false); }
  };

  // Switch the source being edited: confirm when the CURRENT source has
  // unsaved edits (they would be lost), then LOAD the chosen source's blob
  // into the fields (band blob from memory — it is already fetched; tour blob
  // from the dedicated GET route). A declined switch reverts the select.
  const changeSource = async (raw: string): Promise<boolean> => {
    if (!canWrite) return false;
    const next: 'band' | number = raw === '' ? 'band' : Number(raw);
    if (next === target) return false;
    if (dirty && !window.confirm(t('promoContent.switchConfirm'))) return false;
    setError(null);
    try {
      if (next === 'band') {
        applyContent(bandContent);
      } else {
        const r = await apiGetTourPromoContent(next);
        applyContent(r.content);
      }
      setTarget(next);
      setMsg(null);
      return true;
    } catch (e) {
      setError(e instanceof Error ? e.message : t('promoContent.saveFailed'));
      return false;
    }
  };

  const selectSource = async (raw: string) => {
    if (!canWrite) return;
    const next = raw === '' ? null : Number(raw);
    setSavingSource(true); setError(null);
    try {
      await apiPutBandPromoSource(bandId, next);
      setMsg(t('promoContent.sourceSaved'));
    } catch (e) {
      setError(e instanceof Error ? e.message : t('promoContent.saveFailed'));
    } finally { setSavingSource(false); }
  };

  const previewUrl = useMemo(
    () => (token ? `${window.location.origin}/share/promo/${token}` : null),
    [token],
  );

  // The preview feeds PromoContentPage the SAME payload shape the public route
  // returns — with the files' URLs rewritten to the real GUARDED route so the
  // downloads/thumbnails keep working behind the password gate. Never the
  // /static/band-files URL (promo-kit-password-2026.md).
  const previewData: PreviewData = useMemo(() => ({
    token: token || '',
    band: { id: bandId, name: bandName, slug: '', image_url: bandImage },
    files: files.map((f) => ({
      id: Number(f.id),
      filename: String(f.filename || ''),
      url: token ? `/api/share/promo/${encodeURIComponent(token)}/file/${f.id}` : String(f.url || ''),
      size: Number(f.size || 0),
      mime: String(f.mime || ''),
      uploaded_at: String((f as { uploaded_at?: string }).uploaded_at || ''),
    })),
  }), [token, bandId, bandName, bandImage, files]);

  const previewPaneWidth = previewWidth === 'mobile' ? 390 : undefined;

  if (!loaded) return null;

  const editorEl = (
    <PromoContentEditor key={String(target)} value={content} onChange={setContent} files={files} disabled={!canWrite || saving} />
  );

  const previewEl = (
    <div
      style={{
        width: previewPaneWidth,
        maxWidth: '100%',
        margin: previewPaneWidth ? '0 auto' : undefined,
        border: '1px solid var(--border)',
        borderRadius: 8,
        overflowY: 'auto',
        background: 'var(--bg, #141310)',
        // SAME geometry as the rider PDF preview (PdfPreview: a tall
        // full-page pane with internal vertical scroll — never a squashed
        // card). Sandro v2 #4.
        minHeight: 740,
        height: 'calc(100vh - 220px)',
        maxHeight: 1100,
      }}
      data-promo-live-preview
    >
      <PromoContentPage data={previewData as never} content={previewContent} />
    </div>
  );

  return (
    <div style={{ marginBottom: 20 }} data-promo-content>
      <p className="text-dim" style={{ marginBottom: 6 }}>{t('promoContent.sourceLabel')}</p>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <select
          className="form-input"
          style={{ flex: 1, minWidth: 240, maxWidth: 420 }}
          value={target === 'band' ? '' : String(target)}
          disabled={!canWrite || savingSource}
          onChange={(e) => {
            const raw = e.target.value;
            void changeSource(raw).then((ok) => { if (ok) return selectSource(raw); });
          }}
        >
          <option value="">{t('promoContent.sourceBandDefault')}</option>
          {tours.map((tr) => (
            <option key={tr.id} value={tr.id}>{tr.name}</option>
          ))}
        </select>
        {/* Badge: WHICH source the editor (and Save) currently targets. */}
        <span
          className="rc-chip"
          data-promo-source-badge={target === 'band' ? 'band' : String(target)}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
        >
          {target === 'band'
            ? t('promoContent.sourceBandDefault')
            : (tours.find((tr) => tr.id === target)?.name || `#${target}`)}
          {dirty ? ` — ${t('promoContent.unsavedBadge')}` : ''}
        </span>
        {previewUrl && (
          <a className="btn btn-secondary" href={previewUrl} target="_blank" rel="noreferrer" data-promo-preview>
            {t('promoContent.preview')}
          </a>
        )}
      </div>
      <p className="text-dim" style={{ marginTop: 6, fontSize: 'var(--font-xs)' }}>
        {t('promoContent.sourceHint')}
      </p>

      <div style={{ borderTop: '1px solid var(--border)', margin: '14px 0', paddingTop: 14 }}>
        <strong style={{ display: 'block', marginBottom: 10 }}>{t('promoContent.bandEditorTitle')}</strong>
        {narrow ? (
          <>
            <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
              <button
                type="button"
                className={`btn btn-small ${previewTab === 'editor' ? 'btn-primary' : ''}`}
                onClick={() => setPreviewTab('editor')}
              >
                {t('promoContent.tabEditor')}
              </button>
              <button
                type="button"
                className={`btn btn-small ${previewTab === 'preview' ? 'btn-primary' : ''}`}
                onClick={() => setPreviewTab('preview')}
              >
                {t('promoContent.tabPreview')}
              </button>
              {previewTab === 'preview' && (
                <button
                  type="button"
                  className="btn btn-small"
                  style={{ marginLeft: 'auto' }}
                  onClick={() => setPreviewWidth((w) => (w === 'desktop' ? 'mobile' : 'desktop'))}
                >
                  {previewWidth === 'desktop' ? t('promoContent.previewMobile') : t('promoContent.previewDesktop')}
                </button>
              )}
            </div>
            {previewTab === 'editor' ? editorEl : previewEl}
          </>
        ) : (
          <>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginBottom: 6 }}>
              <button
                type="button"
                className="btn btn-small"
                onClick={() => setPreviewWidth((w) => (w === 'desktop' ? 'mobile' : 'desktop'))}
                title={previewWidth === 'desktop' ? t('promoContent.previewMobile') : t('promoContent.previewDesktop')}
              >
                {previewWidth === 'desktop' ? t('promoContent.previewMobile') : t('promoContent.previewDesktop')}
              </button>
            </div>
            <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }} data-promo-split>
              {/* Editor pane: independent scroll, no preview sync. */}
              <div
                style={{
                  flex: 1,
                  minWidth: 0,
                  overflowY: 'auto',
                  minHeight: 740,
                  height: 'calc(100vh - 220px)',
                  maxHeight: 1100,
                  paddingRight: 4,
                }}
              >
                {editorEl}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>{previewEl}</div>
            </div>
          </>
        )}
        <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
          <button className="btn btn-primary" type="button" onClick={() => void save()} disabled={!canWrite || saving}>
            {saving ? t('promoContent.saving') : t('promoContent.save')}
          </button>
          <button className="btn btn-secondary" type="button" onClick={() => void clearContent()} disabled={!canWrite || saving}>
            {t('promoContent.clear')}
          </button>
        </div>
        {msg && <p style={{ color: 'var(--success)', margin: '8px 0 0', fontSize: 'var(--font-sm)' }} data-promo-content-msg>{msg}</p>}
        {error && <p style={{ color: 'var(--danger)', margin: '8px 0 0', fontSize: 'var(--font-sm)' }} role="alert">{error}</p>}
      </div>
    </div>
  );
}
